import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001/api';

const api = axios.create({
  baseURL: API_BASE_URL,
  timeout: 10000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Storage keys constants (no more 'admin_' prefix confusion)
const STORAGE_KEYS = {
  ACCESS_TOKEN: 'access_token',
  REFRESH_TOKEN: 'refresh_token',
  USER_DATA: 'user_data',
  USER_TYPE: 'user_type' // 'admin' or 'volunteer'
};

// Helper functions for token management (defined before use)
const getAccessToken = () => localStorage.getItem(STORAGE_KEYS.ACCESS_TOKEN);
const getRefreshToken = () => localStorage.getItem(STORAGE_KEYS.REFRESH_TOKEN);
const setAccessToken = (token) => localStorage.setItem(STORAGE_KEYS.ACCESS_TOKEN, token);
const setRefreshToken = (token) => localStorage.setItem(STORAGE_KEYS.REFRESH_TOKEN, token);
const getUserType = () => localStorage.getItem(STORAGE_KEYS.USER_TYPE);
const setUserType = (type) => localStorage.setItem(STORAGE_KEYS.USER_TYPE, type);
const clearAuthData = () => {
  Object.values(STORAGE_KEYS).forEach(key => localStorage.removeItem(key));
};

// Reload protection constants
const RELOAD_KEY = 'reload_attempts';
const MAX_RELOADS = 3;
const RELOAD_WINDOW = 10000; // 10 seconds

// Check if we can reload (prevent infinite loops)
const canReload = () => {
  const data = localStorage.getItem(RELOAD_KEY);
  if (!data) return true;

  try {
    const { count, timestamp } = JSON.parse(data);
    // Reset counter if window expired
    if (Date.now() - timestamp > RELOAD_WINDOW) {
      localStorage.removeItem(RELOAD_KEY);
      return true;
    }
    return count < MAX_RELOADS;
  } catch {
    localStorage.removeItem(RELOAD_KEY);
    return true;
  }
};

// Increment reload counter
const incrementReload = () => {
  const data = localStorage.getItem(RELOAD_KEY);
  const current = data ? JSON.parse(data) : { count: 0, timestamp: Date.now() };

  localStorage.setItem(RELOAD_KEY, JSON.stringify({
    count: current.count + 1,
    timestamp: current.timestamp || Date.now()
  }));
};

// Clear reload counter on successful auth
const clearReloadCounter = () => {
  localStorage.removeItem(RELOAD_KEY);
};

// Track if refresh is in progress to prevent multiple simultaneous refreshes
let isRefreshing = false;
let failedQueue = [];

const processQueue = (error, token = null) => {
  failedQueue.forEach(prom => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });
  failedQueue = [];
};

// Request interceptor for logging and auth
api.interceptors.request.use(
  (config) => {
    // Only log in development
    if (process.env.NODE_ENV === 'development') {
      console.log(`API Request: ${config.method?.toUpperCase()} ${config.url}`);
    }

    // Add auth token to all requests except auth endpoints
    if (!config.url?.includes('/auth/')) {
      const token = getAccessToken();
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
    }

    return config;
  },
  (error) => {
    if (process.env.NODE_ENV === 'development') {
      console.error('API Request Error:', error);
    }
    return Promise.reject(error);
  }
);

// Response interceptor for error handling and token refresh
api.interceptors.response.use(
  (response) => {
    return response;
  },
  async (error) => {
    const originalRequest = error.config;

    // Log errors (only in development for non-network errors)
    if (process.env.NODE_ENV === 'development') {
      console.error('API Response Error:', error.response?.data || error.message);
    }

    // Handle network errors
    if (!error.response) {
      if (process.env.NODE_ENV === 'development') {
        console.error('Network error:', error.message);
      }
      return Promise.reject({
        ...error,
        message: error.message || 'Network error. Please check your connection.',
        isNetworkError: true
      });
    }

    // Handle 401 Unauthorized - try to refresh token
    if (error.response?.status === 401 && !originalRequest._retry && error.response?.data?.code === 'TOKEN_EXPIRED') {
      if (isRefreshing) {
        // If refresh is already in progress, queue this request
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        })
          .then(token => {
            originalRequest.headers.Authorization = `Bearer ${token}`;
            return api(originalRequest);
          })
          .catch(err => {
            return Promise.reject(err);
          });
      }

      originalRequest._retry = true;
      isRefreshing = true;

      try {
        const refreshToken = getRefreshToken();
        if (!refreshToken) {
          throw new Error('No refresh token available');
        }

        if (process.env.NODE_ENV === 'development') {
          console.log('Token expired, attempting refresh...');
        }

        // Use correct endpoint based on user type
        let userType = getUserType();
        if (!userType) {
          // If no user type stored, try to detect from old storage (fallback)
          const userData = localStorage.getItem('admin_user') || localStorage.getItem(STORAGE_KEYS.USER_DATA);
          if (userData) {
            try {
              const parsed = JSON.parse(userData);
              const detectedType = parsed.tipo ? 'admin' : 'volunteer';
              setUserType(detectedType);
              userType = detectedType;
            } catch {
              throw new Error('Unable to determine user type');
            }
          } else {
            throw new Error('User type not found');
          }
        }

        const refreshEndpoint = userType === 'admin'
          ? '/auth/admin/refresh'
          : '/auth/volunteer/refresh';

        const refreshResponse = await axios.post(`${API_BASE_URL}${refreshEndpoint}`, {
          refreshToken
        });

        const newAccessToken = refreshResponse.data?.accessToken;
        const newRefreshToken = refreshResponse.data?.refreshToken;

        if (!newAccessToken) {
          throw new Error('Invalid refresh response: missing accessToken');
        }

        setAccessToken(newAccessToken);
        // Update refresh token if provided (token rotation)
        if (newRefreshToken) {
          setRefreshToken(newRefreshToken);
        }

        isRefreshing = false;
        processQueue(null, newAccessToken);
        clearReloadCounter(); // Clear reload counter on successful refresh

        // Retry original request with new token
        originalRequest.headers.Authorization = `Bearer ${newAccessToken}`;
        return api(originalRequest);
      } catch (refreshError) {
        isRefreshing = false;
        processQueue(refreshError, null);

        if (process.env.NODE_ENV === 'development') {
          console.error('Token refresh failed:', refreshError);
        }

        // Refresh failed, clear auth and handle reload
        clearAuthData();

        // Check if we can reload (prevent infinite loops)
        if (canReload()) {
          incrementReload();
          setTimeout(() => {
            window.location.reload();
          }, 100);
        } else {
          // Too many reload attempts, redirect to login
          console.error('Too many reload attempts. Please login manually.');
          window.location.href = '/';
        }

        return Promise.reject(refreshError);
      }
    }

    // Handle other auth errors (non-refreshable)
    if (error.response?.status === 401) {
      // Only reload when a session token existed (real session expiry).
      // Without a token the login screen is already visible - reloading
      // here would restart the app in an infinite reload loop.
      const hadToken = !!getAccessToken();

      if (process.env.NODE_ENV === 'development') {
        console.warn('Authentication failed, clearing tokens');
      }
      clearAuthData();

      if (hadToken) {
        // Check if we can reload (prevent infinite loops)
        if (canReload()) {
          incrementReload();
          setTimeout(() => {
            window.location.reload();
          }, 100);
        } else {
          // Too many reload attempts, redirect to login
          console.error('Too many reload attempts. Please login manually.');
          window.location.href = '/';
        }
      }
    }

    return Promise.reject(error);
  }
);

export const authApi = {
  // Login with Google (tries admin first, then volunteer)
  loginWithGoogle: async (googleToken) => {
    try {
      // Try admin login first
      const response = await api.post('/auth/admin/login', { google_token: googleToken });
      return response;
    } catch (adminError) {
      // If admin login fails (403 or 401), try volunteer login
      if (adminError.response?.status === 403 || adminError.response?.status === 401) {
        const volunteerResponse = await api.post('/auth/volunteer/login', { google_token: googleToken });
        return volunteerResponse;
      }
      // Re-throw if it's a different error
      throw adminError;
    }
  },

  // Login as admin
  loginAsAdmin: (googleToken) => {
    return api.post('/auth/admin/login', { google_token: googleToken });
  },

  // Login as volunteer
  loginAsVolunteer: (googleToken) => {
    return api.post('/auth/volunteer/login', { google_token: googleToken });
  },

  // Logout (uses correct endpoint based on user type)
  logout: async () => {
    const token = getAccessToken();
    const userType = getUserType();

    if (!token) {
      clearAuthData();
      return Promise.resolve();
    }

    try {
      // Use correct endpoint based on user type
      const logoutEndpoint = userType === 'admin'
        ? '/auth/admin/logout'
        : '/auth/volunteer/logout';

      await api.post(logoutEndpoint);
    } catch (error) {
      // Log error but continue with clearing auth
      if (process.env.NODE_ENV === 'development') {
        console.error('Logout error:', error);
      }
    } finally {
      clearAuthData();
      clearReloadCounter(); // Clear reload counter on logout
    }
    return Promise.resolve();
  },

  // Check if user is authenticated
  // Note: This only checks if token exists, not if it's valid
  // The interceptor will handle token validation
  isAuthenticated: () => {
    return !!getAccessToken();
  },

  // Get current user from localStorage
  getCurrentUser: () => {
    try {
      // Try new key first, fallback to old key for compatibility
      const userStr = localStorage.getItem(STORAGE_KEYS.USER_DATA) || localStorage.getItem('admin_user');
      return userStr ? JSON.parse(userStr) : null;
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        console.error('Error parsing user data:', error);
      }
      return null;
    }
  },

  // Get user type (admin or volunteer)
  getUserType: () => {
    return getUserType();
  },

  // Clear all auth data
  clearAuth: clearAuthData,
};

export const rondaApi = {
  // Get active rondas for a specific date (requires auth)
  // @param {string} date - Optional date in YYYY-MM-DD format (defaults to today on backend)
  getActiveRondas: (date = null) => {
    const params = date ? { date } : {};
    return api.get('/rondas/active', { params });
  },
};

export const eventApi = {
  // Get today's events (public endpoint)
  getTodayEvents: () => api.get('/eventos/today'),

  // Get events for a specific date (public endpoint)
  // @param {string} date - Date in YYYY-MM-DD format
  getEventsByDate: (date) => api.get(`/eventos/date/${date}`),
};

export const statisticsApi = {
  // Get statistics with optional filters
  // @param {Object} params - Query parameters (data_inicio, data_fim, ronda_id, entidade_id, limit)
  getStatistics: (params) => api.get('/statistics', { params }),
};

export default api;